/**
 * Community feature data layer — typed fetchers + tolerant normalizers over
 * the Bero API. The backend endpoints are being built in parallel, so every
 * mapper accepts several plausible key spellings and every fetch failure
 * (including 404 on a stub route) surfaces as screen-level empty/error state,
 * never a crash.
 */
import React from 'react';

import { formatINR } from '@bero/tokens';
import { translate, monthShort, dayShort, type TFunc } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';

/* ---- API-value enum → localised label (fall back to the raw value) ----
 * These take the reactive t() from useT() (NOT the module-level translate) so
 * they re-render correctly on a language change even on static, non-fetched
 * content — an effect-synced module singleton renders a step behind there. */
const ISSUE_CAT: Record<string, DictKey> = {
  cleanliness: 'ic_cleanliness', lift: 'ic_lift', noise: 'ic_noise', water: 'ic_water',
  parking: 'ic_parking', electrical: 'ic_electrical', garbage: 'ic_garbage', plumbing: 'ic_plumbing',
  security: 'ic_security', maintenance: 'ic_maintenance', general: 'ic_general', other: 'ic_other',
  housekeeping: 'ic_housekeeping',
};
export function issueCatLabel(raw: string, t: TFunc): string {
  const k = ISSUE_CAT[raw.trim().toLowerCase()];
  return k ? t(k) : raw;
}
const DISC_TAG: Record<string, DictKey> = {
  general: 'dt_general', 'lost & found': 'dt_lost_found', 'lost and found': 'dt_lost_found',
  recommendations: 'dt_recommendations', recommendation: 'dt_recommendations',
  help: 'dt_help', events: 'dt_events', event: 'dt_events', announcements: 'dt_announcements',
  activities: 'dt_activities', activity: 'dt_activities',
};
export function discussionTagLabel(raw: string, t: TFunc): string {
  const k = DISC_TAG[raw.trim().toLowerCase()];
  return k ? t(k) : raw;
}
const CELEB_KIND: Record<string, DictKey> = { birthday: 'ck_birthday', anniversary: 'ck_anniversary' };
export function celebrationKindLabel(raw: string, t: TFunc): string {
  const k = CELEB_KIND[raw.trim().toLowerCase()];
  return k ? t(k) : raw;
}
// vendor categories (API-value enum) — all DB-distinct values, resolved at render.
const VENDOR_CAT: Record<string, DictKey> = {
  electrician: 'vendor_cat_electrician', housekeeping: 'vendor_cat_housekeeping',
  appliance: 'vendor_cat_appliance', gardening: 'vendor_cat_gardening',
  plumbing: 'vendor_cat_plumbing', carpentry: 'vendor_cat_carpentry',
  painting: 'vendor_cat_painting', 'pest control': 'vendor_cat_pest',
};
export function vendorCatLabel(raw: string, t: TFunc): string {
  const k = VENDOR_CAT[raw.trim().toLowerCase()];
  return k ? t(k) : raw;
}
// event categories (API-value enum) — resolved at render.
const EVENT_CAT: Record<string, DictKey> = {
  festival: 'ev_cat_festival', sports: 'ev_cat_sports', kids: 'ev_cat_kids',
  cultural: 'ev_cat_cultural', meeting: 'ev_cat_meeting', general: 'ev_cat_general',
};
export function eventCatLabel(raw: string, t: TFunc): string {
  const k = EVENT_CAT[raw.trim().toLowerCase()];
  return k ? t(k) : raw;
}

type Raw = Record<string, unknown>;

/* ---------------- tolerant readers ---------------- */

function str(o: Raw, keys: string[], fb = ''): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return fb;
}

function num(o: Raw, keys: string[], fb = 0): number {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v !== '' && !Number.isNaN(Number(v))) return Number(v);
  }
  return fb;
}

function bool(o: Raw, keys: string[], fb = false): boolean {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'boolean') return v;
  }
  return fb;
}

/** Unwrap a list body: raw array, or {items|polls|events|…: []}. */
function items(body: unknown, extraKeys: string[] = []): Raw[] {
  if (Array.isArray(body)) return body.filter((x): x is Raw => !!x && typeof x === 'object');
  if (body && typeof body === 'object') {
    const b = body as Raw;
    for (const k of ['items', 'results', 'data', ...extraKeys]) {
      const v = b[k];
      if (Array.isArray(v)) return v.filter((x): x is Raw => !!x && typeof x === 'object');
    }
  }
  return [];
}

/** Unwrap a detail body: the object itself or {poll|event|…: {...}}. */
function one(body: unknown, key: string): Raw {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const b = body as Raw;
    const nested = b[key];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) return nested as Raw;
    return b;
  }
  return {};
}

/* ---------------- dates ---------------- */

// month/day names via i18n monthShort/dayShort (localised with active language)

function toDate(v: unknown): Date | null {
  if (typeof v !== 'string' && typeof v !== 'number') return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateOf(o: Raw, keys: string[]): Date | null {
  for (const k of keys) {
    const d = toDate(o[k]);
    if (d) return d;
  }
  return null;
}

export function timeAgo(d: Date): string {
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return translate('time_now');
  const m = Math.floor(s / 60);
  if (m < 60) return translate('time_m', { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return translate('time_h', { n: h });
  const days = Math.floor(h / 24);
  if (days < 7) return translate('time_d', { n: days });
  return `${d.getDate()} ${monthShort(d.getMonth())}`;
}

/** "Sat, 9 Nov · 7:00 PM" */
function formatWhen(d: Date): string {
  let h = d.getHours();
  const am = h < 12 ? 'AM' : 'PM';
  h = h % 12 || 12;
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dayShort(d.getDay())}, ${d.getDate()} ${monthShort(d.getMonth())} · ${h}:${mm} ${am}`;
}

function closesLabel(d: Date, closed: boolean): string {
  if (closed) return translate('poll_closed');
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  if (days <= 0) return translate('poll_closes_today');
  if (days === 1) return translate('poll_closes_tomorrow');
  return translate('poll_closes_in', { n: days });
}

export const pct = (n: number, total: number): number => (total > 0 ? Math.round((n / total) * 100) : 0);

/* ---------------- polls ---------------- */

export type PollOption = { id: string; label: string; votes: number };

export type Poll = {
  id: string;
  question: string;
  scope: string;
  anonymous: boolean;
  status: 'active' | 'closed';
  deadline: string;
  votes: number;
  total: number;
  postedBy: string;
  options: PollOption[];
  myOptionId: string | null;
};

function pollFromApi(raw: Raw, idx = 0): Poll {
  // Live API shape: options is a plain string[] (no per-option vote counts
  // inline) with a parallel `counts` number[] aligned by index, plus
  // top-level `totalVotes`/`eligibleVoters`. Zip counts onto options here so
  // every downstream pct()/sum computation sees real per-option numbers
  // instead of the always-0 default that caused "0% on every option" and
  // "N of M voted" reading past its own denominator.
  const rawOpts = Array.isArray(raw.options) ? (raw.options as unknown[]) : [];
  const rawCounts = Array.isArray(raw.counts) ? (raw.counts as unknown[]) : [];
  const countAt = (i: number): number | null => {
    const v = rawCounts[i];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v !== '' && !Number.isNaN(Number(v))) return Number(v);
    return null;
  };
  const options: PollOption[] = rawOpts.map((o, i) => {
    const zipped = countAt(i);
    if (typeof o === 'string') {
      return { id: String(i), label: o, votes: zipped ?? 0 };
    }
    if (Array.isArray(o)) {
      const inline = typeof o[1] === 'number' ? o[1] : Number(o[1] ?? 0) || 0;
      return { id: String(i), label: String(o[0] ?? ''), votes: zipped ?? inline };
    }
    const r = (o && typeof o === 'object' ? o : {}) as Raw;
    const inline = num(r, ['votes', 'voteCount', 'count']);
    return {
      id: str(r, ['id', 'optionId'], String(num(r, ['id', 'optionId'], i))),
      label: str(r, ['label', 'text', 'title', 'option']),
      votes: zipped ?? inline,
    };
  });

  const statusStr = str(raw, ['status']).toLowerCase();
  const closesAt = dateOf(raw, ['closesAt', 'deadlineAt', 'endsAt', 'closeAt']);
  const closed =
    statusStr === 'closed' || bool(raw, ['isClosed', 'closed']) || (!!closesAt && closesAt.getTime() < Date.now());

  const optionSum = options.reduce((s, o) => s + o.votes, 0);
  // votes = numerator ("N of M voted"), total = denominator (eligible
  // voters, NOT a running vote total) — the API's `totalVotes`/
  // `eligibleVoters` are the source of truth; clamp total up so the
  // denominator can never read lower than the numerator.
  const votes = num(raw, ['totalVotes', 'votes', 'voteCount'], optionSum);
  const eligible = num(raw, ['eligibleVoters', 'total', 'eligible', 'totalResidents', 'electorate'], Math.max(votes, optionSum));
  const total = Math.max(eligible, votes);

  let mine: string | null = null;
  const mv = raw.myVote ?? raw.myOptionId ?? raw.mine ?? raw.votedOptionId;
  if (typeof mv === 'number' && options[mv]) {
    mine = options[mv].id;
  } else if (typeof mv === 'string' && mv !== '') {
    mine = options.some((o) => o.id === mv) ? mv : mv;
  }

  return {
    id: str(raw, ['id'], String(num(raw, ['id'], idx))),
    question: str(raw, ['question', 'q', 'title']),
    scope: str(raw, ['scope', 'audience'], 'All wings'),
    anonymous: bool(raw, ['anonymous', 'anon', 'isAnonymous']),
    status: closed ? 'closed' : 'active',
    deadline: (() => {
      const rawDl = str(raw, ['deadline']);
      const asDate = toDate(rawDl);
      if (asDate) return closesLabel(asDate, closed);
      return rawDl || (closesAt ? closesLabel(closesAt, closed) : closed ? translate('poll_closed') : translate('poll_open'));
    })(),
    votes,
    total,
    postedBy: str(raw, ['postedBy', 'author', 'by', 'createdBy'], 'Committee'),
    options,
    myOptionId: mine,
  };
}

export async function fetchPolls(): Promise<Poll[]> {
  const body = await api.get<unknown>('/api/polls');
  // Live API splits into {active, closed}; flat {items} shape kept as fallback.
  const flat = items(body, ['polls']);
  const rows = flat.length ? flat : [...items(body, ['active']), ...items(body, ['closed'])];
  return rows.map((r, i) => pollFromApi(r, i));
}

export async function fetchPoll(id: string): Promise<Poll> {
  const p = pollFromApi(one(await api.get<unknown>(`/api/polls/${id}`), 'poll'));
  if (!p.question && p.options.length === 0) throw new Error('Poll not found');
  return p;
}

export function votePoll(id: string, optionId: string, optionIndex: number) {
  return api.post<unknown>(`/api/polls/${id}/vote`, { optionId, optionIndex });
}

/** Optimistic local mutation after casting a vote. Clamps `total` up so the
 * "N of M voted" denominator can never read lower than the incremented
 * numerator (previously produced e.g. "4 of 3 voted"). */
export function withVote(p: Poll, optionId: string): Poll {
  const votes = p.votes + 1;
  return {
    ...p,
    myOptionId: optionId,
    votes,
    total: Math.max(p.total, votes),
    options: p.options.map((o) => (o.id === optionId ? { ...o, votes: o.votes + 1 } : o)),
  };
}

/* ---------------- events ---------------- */

export type Rsvp = 'GOING' | 'MAYBE' | 'NO';

export type SocietyEvent = {
  id: string;
  title: string;
  when: string;
  day: string;
  month: string;
  venue: string;
  category: string;
  description: string;
  past: boolean;
  pending: boolean;
  cover: string | null;
  going: number;
  maybe: number;
  notGoing: number;
  roster: string[];
  myRsvp: Rsvp | null;
};

function rsvpOf(v: unknown): Rsvp | null {
  if (typeof v !== 'string') return null;
  const s = v.toUpperCase();
  if (s === 'GOING' || s === 'YES') return 'GOING';
  if (s === 'MAYBE') return 'MAYBE';
  if (s === 'NO' || s === 'DECLINED' || s === 'CANT_GO' || s === 'NOT_GOING') return 'NO';
  return null;
}

function eventFromApi(raw: Raw, idx = 0): SocietyEvent {
  const startsAt = dateOf(raw, ['startsAt', 'startAt', 'date', 'scheduledAt', 'eventAt']);
  const when = str(raw, ['when'], startsAt ? formatWhen(startsAt) : '');

  let day = '';
  let month = '';
  if (startsAt) {
    day = String(startsAt.getDate());
    month = monthShort(startsAt.getMonth());
  } else {
    const m = when.match(/(\d{1,2})\s+([A-Za-z]{3})/);
    if (m) {
      day = m[1];
      month = m[2];
    }
  }

  const counts =
    raw.counts && typeof raw.counts === 'object' && !Array.isArray(raw.counts) ? (raw.counts as Raw) : raw;

  const rosterRaw = Array.isArray(raw.roster) ? raw.roster : Array.isArray(raw.attendees) ? raw.attendees : [];
  const roster = (rosterRaw as unknown[])
    .map((r) => (typeof r === 'string' ? r : r && typeof r === 'object' ? str(r as Raw, ['name', 'fullName']) : ''))
    .filter((s) => s.length > 0);

  return {
    id: str(raw, ['id'], String(num(raw, ['id'], idx))),
    title: str(raw, ['title', 'name']),
    when,
    day,
    month,
    venue: str(raw, ['venue', 'location', 'place']),
    category: str(raw, ['category', 'cat'], 'General'),
    description: str(raw, ['description', 'desc', 'body']),
    past: bool(raw, ['past', 'isPast'], !!startsAt && startsAt.getTime() < Date.now()),
    pending: bool(raw, ['pending'], str(raw, ['status']).toUpperCase() === 'PENDING'),
    cover: str(raw, ['cover', 'coverUrl', 'image']) || null,
    going: num(counts, ['going', 'goingCount', 'yes']),
    maybe: num(counts, ['maybe', 'maybeCount']),
    notGoing: num(counts, ['notGoing', 'no', 'noCount', 'declined']),
    roster,
    myRsvp: rsvpOf(raw.myRsvp ?? raw.rsvp ?? raw.myStatus),
  };
}

export async function fetchEvents(): Promise<SocietyEvent[]> {
  const body = await api.get<unknown>('/api/events');
  // Live API splits into {upcoming, past}; flat {items} shape kept as fallback.
  const flat = items(body, ['events']);
  const rows = flat.length ? flat : [...items(body, ['upcoming']), ...items(body, ['past'])];
  return rows.map((r, i) => eventFromApi(r, i));
}

export async function fetchEvent(id: string): Promise<SocietyEvent> {
  try {
    const e = eventFromApi(one(await api.get<unknown>(`/api/events/${id}`), 'event'));
    if (e.title) return e;
    throw new Error('Event not found');
  } catch {
    // Detail endpoint may not exist yet — fall back to finding it in the list.
    const all = await fetchEvents();
    const found = all.find((e) => e.id === String(id));
    if (!found) throw new Error('Event not found');
    return found;
  }
}

export function rsvpEvent(id: string, status: Rsvp) {
  return api.post<unknown>(`/api/events/${id}/rsvp`, { status });
}

export type NewEvent = { title: string; category: string; date: string; time: string; venue: string; description: string };

export function createEvent(input: NewEvent) {
  return api.post<unknown>('/api/events', input);
}

/** Optimistic local mutation after changing an RSVP. */
export function withRsvp(e: SocietyEvent, status: Rsvp): SocietyEvent {
  const counts: Record<Rsvp, number> = { GOING: e.going, MAYBE: e.maybe, NO: e.notGoing };
  if (e.myRsvp) counts[e.myRsvp] = Math.max(0, counts[e.myRsvp] - 1);
  counts[status] += 1;
  return { ...e, myRsvp: status, going: counts.GOING, maybe: counts.MAYBE, notGoing: counts.NO };
}

/* ---------------- issues ---------------- */

export type IssueStatus = 'open' | 'progress' | 'urgent' | 'resolved';
export type IssueVisibility = 'PERSONAL' | 'COMMUNITY';

export type Issue = {
  id: string;
  code: string;
  visibility: IssueVisibility;
  category: string;
  title: string;
  body: string;
  status: IssueStatus;
  when: string;
  notify: string;
  upvotes: number;
  upvoted: boolean;
  comments: number;
  raisedBy: string;
  photos: number;
  /** Raw ISO timestamps + admin notes for the honest, derived status
   * timeline — the API has no status-change history, so the timeline can
   * only ever show "Reported" (createdAt) and, if the status has moved on,
   * the current status (updatedAt) — never fabricated intermediate steps. */
  createdAt: string | null;
  updatedAt: string | null;
  adminNotes: string;
};

function issueStatusOf(v: string): IssueStatus {
  const s = v.toLowerCase().replace(/[\s-]/g, '_');
  if (s === 'in_progress' || s === 'progress') return 'progress';
  if (s === 'urgent') return 'urgent';
  if (s === 'resolved' || s === 'closed' || s === 'done') return 'resolved';
  return 'open';
}

function issueFromApi(raw: Raw, fallbackVisibility: IssueVisibility = 'COMMUNITY', idx = 0): Issue {
  const created = dateOf(raw, ['createdAt', 'raisedAt']);
  const vis = str(raw, ['visibility', 'scope']).toUpperCase();
  const commentsRaw = raw.comments;
  const comments = Array.isArray(commentsRaw) ? commentsRaw.length : num(raw, ['comments', 'commentCount'], 0);
  const photosRaw = raw.photos;
  return {
    id: str(raw, ['id'], String(num(raw, ['id'], idx))),
    code: str(raw, ['code', 'ref'], str(raw, ['id'], String(num(raw, ['id'], idx)))),
    visibility: vis === 'PERSONAL' ? 'PERSONAL' : vis === 'COMMUNITY' ? 'COMMUNITY' : fallbackVisibility,
    category: str(raw, ['category', 'cat'], 'General'),
    title: str(raw, ['title', 'subject']),
    body: str(raw, ['body', 'description', 'desc']),
    status: issueStatusOf(str(raw, ['status'], 'open')),
    when: str(raw, ['when'], created ? timeAgo(created) : ''),
    notify: str(raw, ['notify', 'notifyLabel'], notifyLabel(str(raw, ['notifyScope'], ''))),
    upvotes: num(raw, ['upvotes', 'up', 'upvoteCount']),
    upvoted: bool(raw, ['upvoted', 'hasUpvoted', 'myUpvote']),
    comments,
    raisedBy: str(raw, ['raisedBy', 'author', 'by', 'reporterName']),
    photos: Array.isArray(photosRaw) ? photosRaw.length : num(raw, ['photoCount'], 0),
    createdAt: str(raw, ['createdAt']) || null,
    updatedAt: str(raw, ['updatedAt']) || null,
    adminNotes: str(raw, ['adminNotes']),
  };
}

export function notifyLabel(scope: string): string {
  const raw = scope.trim();
  if (!raw) return raw;
  // Seed stores formatted strings ("Wing B residents notified"); the report form
  // sends enum scopes ("My wing"/"All residents"/"Helpdesk"). Handle both.
  const wing = raw.match(/wing\s+([a-z0-9]+)/i);
  if (wing) return translate('issue_notify_wing_x', { wing: wing[1].toUpperCase() });
  const s = raw.toUpperCase().replace(/[\s-]/g, '_');
  if (s === 'MY_WING' || s === 'WING') return translate('issue_notify_wing');
  if (s.startsWith('ALL')) return translate('issue_notify_all');
  if (s.includes('HELPDESK')) return translate('issue_notify_helpdesk');
  return scope;
}

export async function fetchIssues(visibility: IssueVisibility): Promise<Issue[]> {
  return items(await api.get<unknown>(`/api/issues?visibility=${visibility}`), ['issues'])
    .map((r, i) => issueFromApi(r, visibility, i))
    .filter((i) => i.visibility === visibility);
}

export async function fetchIssue(id: string): Promise<Issue> {
  try {
    const it = issueFromApi(one(await api.get<unknown>(`/api/issues/${id}`), 'issue'));
    if (it.title) return it;
    throw new Error('Issue not found');
  } catch {
    // Detail endpoint may not exist yet — search both lists.
    const [community, personal] = await Promise.all([
      fetchIssues('COMMUNITY').catch(() => [] as Issue[]),
      fetchIssues('PERSONAL').catch(() => [] as Issue[]),
    ]);
    const found = [...community, ...personal].find((i) => i.id === String(id) || i.code === String(id));
    if (!found) throw new Error('Issue not found');
    return found;
  }
}

export function upvoteIssue(id: string) {
  return api.post<unknown>(`/api/issues/${id}/upvote`);
}

export function postIssueComment(id: string, body: string) {
  return api.post<unknown>(`/api/issues/${id}/comments`, { body });
}

export type NewIssue = {
  title: string;
  category: string;
  visibility: IssueVisibility;
  notifyScope: string;
  description: string;
  photos?: string | null; // base64 data-URI
};

export function createIssue(input: NewIssue) {
  return api.post<unknown>('/api/issues', input);
}

/** Optimistic upvote toggle. */
export function withUpvote(i: Issue): Issue {
  return { ...i, upvoted: !i.upvoted, upvotes: Math.max(0, i.upvotes + (i.upvoted ? -1 : 1)) };
}

/* ---------------- vendors ---------------- */

export type Vendor = {
  id: string;
  name: string;
  category: string;
  phone: string;
  note: string;
  pinned: boolean;
  endorsements: number;
  endorsed: boolean;
};

function vendorFromApi(raw: Raw, idx = 0): Vendor {
  return {
    id: str(raw, ['id'], String(num(raw, ['id'], idx))),
    name: str(raw, ['name', 'title']),
    category: str(raw, ['category', 'cat'], 'General'),
    phone: str(raw, ['phone', 'phoneNumber', 'contact']),
    note: str(raw, ['note', 'notes', 'description', 'body']),
    pinned: bool(raw, ['pinned', 'isPinned']),
    endorsements: num(raw, ['endorsements', 'endorsementCount', 'endorseCount']),
    endorsed: bool(raw, ['endorsed', 'myEndorsement', 'hasEndorsed']),
  };
}

export async function fetchVendors(): Promise<Vendor[]> {
  return items(await api.get<unknown>('/api/vendors'), ['vendors']).map((r, i) => vendorFromApi(r, i));
}

export async function fetchVendor(id: string): Promise<Vendor> {
  try {
    const v = vendorFromApi(one(await api.get<unknown>(`/api/vendors/${id}`), 'vendor'));
    if (v.name) return v;
    throw new Error('Vendor not found');
  } catch {
    const all = await fetchVendors();
    const found = all.find((v) => v.id === String(id));
    if (!found) throw new Error('Vendor not found');
    return found;
  }
}

export function endorseVendor(id: string) {
  return api.post<unknown>(`/api/vendors/${id}/endorse`);
}

/* ---------------- discussions: classifieds / lost & found / celebrations ---------------- */

export type Classified = { id: string; title: string; price: string; tag: string; by: string };

function classifiedFromApi(raw: Raw, idx = 0): Classified {
  const priceRaw = raw.price ?? raw.amount;
  const price =
    typeof priceRaw === 'number' ? formatINR(priceRaw) : typeof priceRaw === 'string' ? priceRaw : '';
  return {
    id: str(raw, ['id'], String(num(raw, ['id'], idx))),
    title: str(raw, ['title', 'name']),
    price,
    tag: str(raw, ['tag', 'category', 'cat'], 'General'),
    by: str(raw, ['by', 'flat', 'flatCode', 'postedBy', 'author']),
  };
}

export async function fetchClassifieds(): Promise<Classified[]> {
  return items(await api.get<unknown>('/api/discussions/classifieds'), ['classifieds', 'listings']).map((r, i) =>
    classifiedFromApi(r, i),
  );
}

export type LostFoundItem = { id: string; type: 'LOST' | 'FOUND'; title: string; where: string; by: string; when: string };

function lostFoundFromApi(raw: Raw, idx = 0): LostFoundItem {
  const created = dateOf(raw, ['createdAt']);
  const t = str(raw, ['type', 'kind']).toUpperCase();
  return {
    id: str(raw, ['id'], String(num(raw, ['id'], idx))),
    type: t === 'FOUND' ? 'FOUND' : 'LOST',
    title: str(raw, ['title', 'name']),
    where: str(raw, ['where', 'location', 'place']),
    by: str(raw, ['by', 'flat', 'flatCode', 'postedBy', 'author']),
    when: str(raw, ['when'], created ? timeAgo(created) : ''),
  };
}

export async function fetchLostFound(): Promise<LostFoundItem[]> {
  return items(await api.get<unknown>('/api/discussions/lost-found'), ['lostFound']).map((r, i) =>
    lostFoundFromApi(r, i),
  );
}

export type Celebration = {
  id: string; name: string; flat: string; date: string; kind: string;
  wishCount: number; wished: boolean;
};

function celebrationFromApi(raw: Raw, idx = 0): Celebration {
  const at = dateOf(raw, ['date', 'at', 'celebratedAt']);
  return {
    id: str(raw, ['id'], String(num(raw, ['id'], idx))),
    name: str(raw, ['name', 'title']),
    flat: str(raw, ['flat', 'flatCode']),
    date: str(raw, ['dateLabel', 'when'], at ? `${at.getDate()} ${monthShort(at.getMonth())}` : str(raw, ['date'])),
    kind: str(raw, ['kind', 'type'], 'Birthday'),
    wishCount: num(raw, ['wishCount', 'wishes']),
    wished: bool(raw, ['hasWished', 'wished']),
  };
}

export async function fetchCelebrations(): Promise<Celebration[]> {
  return items(await api.get<unknown>('/api/discussions/celebrations'), ['celebrations', 'birthdays']).map((r, i) =>
    celebrationFromApi(r, i),
  );
}

export function wishCelebration(id: string) {
  return api.post<{ ok: boolean; hasWished: boolean; wishCount: number }>(`/api/discussions/celebrations/${id}/wish`);
}

/** Optimistic local toggle after wishing/un-wishing a celebration. */
export function withWish(c: Celebration): Celebration {
  return { ...c, wished: !c.wished, wishCount: Math.max(0, c.wishCount + (c.wished ? -1 : 1)) };
}

/* ---------------- fetch hook ---------------- */

/**
 * Collapses an arbitrary-length deps array into a version number that only
 * changes when the deps shallow-differ, so it can be passed to useCallback
 * below as a literal-array dependency (callers still pass a plain deps list).
 */
function useDepsVersion(deps: React.DependencyList): number {
  const [tracked, setTracked] = React.useState({ deps, version: 0 });
  const changed = deps.length !== tracked.deps.length || deps.some((d, i) => !Object.is(d, tracked.deps[i]));
  if (changed) setTracked({ deps, version: tracked.version + 1 });
  return tracked.version;
}

export function useFetch<T>(load: () => Promise<T>, deps: React.DependencyList) {
  const [data, setData] = React.useState<T | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const seq = React.useRef(0);
  const depsVersion = useDepsVersion(deps);

  // deps intentionally control identity; `load` is recreated each render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refetch = React.useCallback(async () => {
    const id = ++seq.current;
    setLoading(true);
    setError(false);
    try {
      const d = await load();
      if (id !== seq.current) return;
      setData(d);
      setLoading(false);
    } catch {
      if (id !== seq.current) return;
      setData(null);
      setError(true);
      setLoading(false);
    }
  }, [depsVersion]);

  React.useEffect(() => {
    void Promise.resolve().then(() => refetch());
  }, [refetch]);

  const mutate = React.useCallback((updater: (prev: T | null) => T | null) => {
    setData(updater);
  }, []);

  return { data, loading, error, refetch, mutate };
}
